import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import ReactDOM from 'react-dom';
import ContextStore from '../../hooks/ContextStore';
import { IProductGroupTableData } from '../../interfaces/formData/ProductGroupTableData.interace';
import { IEditProductGroupFormData } from '../../interfaces/formData/EditProductGroupFormData.interface';
import { dispatch } from '../../dispatch/dispatch';
import { actions } from '../../dispatch';
import { IProductGroupResponse } from '../../interfaces/responses/ProductGroupResponse.interface';
import { IErrorResponse } from '../../interfaces/responses/ErrorResponse.interface';
import { IProductResponse } from '../../interfaces/responses/ProductResponse.interface';

interface IEditProductModalProps {
  setShowModal: Dispatch<SetStateAction<boolean>>;
  productGroup: IProductGroupTableData;
}

export const EditProductModal: FC<IEditProductModalProps> = ({ setShowModal, productGroup }) => {
  const [formData, setFormData] = useState<IEditProductGroupFormData>({
    _id: productGroup._id,
    name: productGroup.name,
    skus: productGroup.skus.map(s => s.sku),
    buyingRate: productGroup.buyingRate,
    packageFees: productGroup.packageFees,
    transportFees: productGroup.transportFees,
    storageFees: productGroup.storageFees,
    jumiaCommission: productGroup.jumiaCommission ?? 15,
    fraisTraitement: productGroup.fraisTraitement ?? 6,
    stockAvailable: productGroup.stockAvailable,
  });
  const { contextStore, setContextStore } = useContext(ContextStore);
  const ref = useRef<HTMLFormElement>(null);
  const [showForm, setShowForm] = useState<boolean>(true);

  useEffect(() => {
    const checkIfClickedOutside = (e: any) => {
      if (showForm && ref.current && !ref.current.contains(e.target)) {
        setShowForm(false);
        setShowModal(false);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);
    return () => document.removeEventListener('mousedown', checkIfClickedOutside);
  }, [showForm]);

  const onClickSubmit = async (e: React.SyntheticEvent) => {
    contextStore.setShowSpinner(true);
    e.preventDefault();
    const productGroupEditResponse: IProductGroupResponse | IErrorResponse =
      await dispatch(actions.editProductGroup, { ...formData }, contextStore.token, { id: formData._id });
    if ('error' in productGroupEditResponse) {
      contextStore.setShowSpinner(false);
      return console.log(productGroupEditResponse.error.message);
    }
    const vProductGroups = contextStore.productGroups;
    const index = vProductGroups.findIndex((pg) => pg._id === productGroupEditResponse._id);
    vProductGroups[index] = productGroupEditResponse;
    const productGroupProductsResponse: Array<IProductResponse> | IErrorResponse =
      await dispatch(actions.getProductGroupProducts,
        { fromDate: contextStore.date.fromDate, toDate: contextStore.date.toDate },
        contextStore.token,
        { productGroupId: formData._id }
      );
    if ('error' in productGroupProductsResponse) {
      contextStore.setShowSpinner(false);
      return console.log(productGroupProductsResponse.error.message);
    }
    const vProducts = contextStore.products;
    productGroupProductsResponse.map((product) => {
      const idx = vProducts.findIndex((vp) => vp._id === product._id);
      if (idx !== -1) vProducts[idx] = product;
    });
    contextStore.overviewTrigger(vProducts);
    contextStore.productTrigger(vProducts);
    contextStore.productGroupTrigger(vProductGroups, vProducts);
    contextStore.setShowSpinner(false);
    setContextStore((past: any) => ({ ...past, products: vProducts, productGroups: vProductGroups }));
    setShowModal(false);
  };

  const onChangeFormData = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const onClickCancel = (e: React.SyntheticEvent) => { e.preventDefault(); setShowModal(false); };

  const fields = [
    { label: 'Name', name: 'name', type: 'text', value: formData.name },
    { label: 'Buying Price', name: 'buyingRate', type: 'number', value: formData.buyingRate },
    { label: 'Transport Fees', name: 'transportFees', type: 'number', value: formData.transportFees },
    { label: 'Storage Fees', name: 'storageFees', type: 'number', value: formData.storageFees },
    { label: 'Package Fees', name: 'packageFees', type: 'number', value: formData.packageFees },
    { label: 'Stock Available', name: 'stockAvailable', type: 'number', value: formData.stockAvailable },
  ];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <form ref={ref} onSubmit={onClickSubmit}
        className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-base font-bold text-foreground">Edit Product Group</h2>
        </div>
        <div className="px-6 py-4 space-y-3">
          {fields.map(({ label, name, type, value }) => (
            <div key={name}>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{label}</label>
              <input
                type={type} name={name} value={value as any}
                onChange={onChangeFormData}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-border bg-muted/30">
          <button type="button" onClick={onClickCancel}
            className="rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent transition-colors">
            Cancel
          </button>
          <button type="submit"
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 transition-colors">
            Save Changes
          </button>
        </div>
      </form>
    </div>,
    document.getElementById('editProduct')!
  );
};
